import { sessionStore } from '@features/auth/services/sessionStore'
import { verifyMasterPasswordMatchesSession } from '@features/auth/services/verifyMasterPassword'
import { ApiError } from '@services/apiClient'
import { deleteAccount } from '../api/deleteAccountApi'

/**
 * アカウントの削除フロー (Settings § account / DELETE /api/users/me)。
 *
 * 設計判断:
 *   - 入力検証: master password を SK 復号で検証 (zero-knowledge を維持、サーバには送らない)。
 *     成功時のみ DELETE /api/users/me を呼ぶ。
 *   - 成功時はサーバ応答 (204) 受領後に sessionStore.clear() を呼び、in-memory の SK を即 zeroize。
 *     呼出側 (UI) は本関数の resolve を契機に #login へ遷移する。
 *   - 失敗時は session を残す (UI 上で再試行 / キャンセル可能)。
 *
 * セキュリティ要件 (CLAUDE.md §2):
 *   - master password はサーバに送らない (本関数内でも保存しない、verify 後すぐ捨てる)
 *   - 削除済ユーザーで refresh が走ると 401 → sessionStore.clear() 経由で UNAUTH に落ちる
 *     (authRefresh.ts §refresh 失敗時のセマンティクス) ため、二重防御。
 */

export class DeleteAccountError extends Error {
  public readonly code:
    | 'no_session'
    | 'wrong_password'
    | 'too_many_requests'
    | 'unauthorized'
    | 'network'
    | 'generic'

  public constructor(code: DeleteAccountError['code']) {
    super(code)
    this.code = code
    this.name = 'DeleteAccountError'
  }
}

export const deleteAccountFlow = async (masterPassword: string): Promise<void> => {
  const state = sessionStore.getState()
  if (state.kind !== 'unlocked') {
    throw new DeleteAccountError('no_session')
  }

  const ok = await verifyMasterPasswordMatchesSession(masterPassword)
  if (!ok) {
    throw new DeleteAccountError('wrong_password')
  }

  try {
    await deleteAccount()
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      if (err.status === 401) throw new DeleteAccountError('unauthorized')
      if (err.status === 429) throw new DeleteAccountError('too_many_requests')
      throw new DeleteAccountError('network')
    }
    throw new DeleteAccountError('generic')
  }

  // サーバ削除成功 → in-memory SK を即 zeroize + UNAUTH 化
  sessionStore.clear()
}
